import FlowApiClient from '../backend/sub_api_clients/FlowApiClient'
import { snackbar } from '../components/services/SnackbarService'
import affirm from '../core/Affirm'
import { IFlowAnimationGroup } from '../definitions/FlowAnimation'
import { IFlow } from '../definitions/Nodes'
import { IExecutionEnvironment, IFlowRunConfig } from '../definitions/NodesExecution'
import EventsEngine from '../engines/EventsEngine'
import Helper from '../helpers/Helper'
import FlowAnimationManager from './FlowAnimationsManager'
import FlowUIManager from './FlowUIManager'
import { Queue } from './QueueManager'

class FlowExecutionManagerClass {
    private MAX_ATTEMPTS = 3
    private POLLING_INTERVAL = 1000
    isPolling = false
    queue: Queue<IFlowAnimationGroup> = null
    execEnv: IExecutionEnvironment = null

    sendFlowAnimation = (newExecEnv: IExecutionEnvironment) => {
        const animGroup: IFlowAnimationGroup = { animations: [], depth: null }
        const newAnimGroup: IFlowAnimationGroup = { animations: [], depth: null }
        affirm(this.execEnv === null || this.execEnv.uuid === newExecEnv.uuid, 'Environment and prev Environment do not match')
        const prevExecEnv = this.execEnv

        for (let i = 0; i < newExecEnv.memories.length; i++) {
            const memory = newExecEnv.memories[i]
            const delay = memory.depth > 0 ? FlowUIManager.EDGE_ANIMATION_DURATION_MS : 0
            const prevMemory = prevExecEnv?.memories.find(x => x.uuid === memory.uuid)
            // console.log('prev', memory, prevMemory)
            if ((!prevMemory || prevMemory.status === 'waiting') && memory.status === 'started') {
                const rng = Helper.generateUUID()
                for (let i = 0; i < memory.node.inputs.length; i++) {
                    const input = memory.node.inputs[i]
                    const edgeId = FlowUIManager._encodeEdge(memory.node, input)
                    animGroup.animations.push({ uuid: rng, nodeId: memory.node.uuid, nodeName: memory.node.name, edgeId: edgeId, animation: 'executing' })
                }
                animGroup.animations.push({ uuid: rng, nodeId: memory.node.uuid, nodeName: memory.node.name, animation: 'executing', delay })
            } else if ((!prevMemory || prevMemory.status === 'started') && memory.status === 'completed') {
                if ((!prevMemory || prevMemory.status === 'completed') && memory.status === 'completed') {
                    for (let i = 0; i < memory.node.inputs.length; i++) {
                        const input = memory.node.inputs[i]
                        const edgeId = FlowUIManager._encodeEdge(memory.node, input)
                        newAnimGroup.animations.push({ uuid: Helper.generateUUID(), nodeId: memory.node.uuid, nodeName: memory.node.name, edgeId: edgeId, animation: 'executing' })
                    }
                    newAnimGroup.animations.push({ uuid: Helper.generateUUID(), nodeId: memory.node.uuid, nodeName: memory.node.name, animation: 'executing', delay })
                }
                animGroup.animations.push({ uuid: Helper.generateUUID(), nodeId: memory.node.uuid, nodeName: memory.node.name, animation: 'completed' })
            } else if ((!prevMemory || prevMemory.status === 'started') && memory.status === 'failed') {
                animGroup.animations.push({ uuid: Helper.generateUUID(), nodeId: memory.node.uuid, nodeName: memory.node.name, animation: 'failed' })
            }
        }

        if (newAnimGroup.animations.length > 0) this.queue?.enqueue(newAnimGroup)
        if (animGroup.animations.length > 0) this.queue?.enqueue(animGroup)
        this.execEnv = newExecEnv
    }

    processAnimationsQueue = (onEndAnimation: () => void) => {
        const toPlay = this.queue.dequeue()
        // console.log('animations1', toPlay?.animations)
        if (toPlay)
            EventsEngine.publish('flow-animation', toPlay)

        if (this.isPolling || this.queue.size() > 0) {
            let sleepMS = 0

            if (toPlay === null || toPlay === undefined) {
                sleepMS = 100
            } else {
                sleepMS = FlowUIManager.EDGE_ANIMATION_DURATION_MS + (toPlay.animations.find(x => x.delay && x.delay > 0)?.delay ?? 0)
            }

            setTimeout(() => {
                this.processAnimationsQueue(onEndAnimation)
            }, sleepMS)
        } else {
            onEndAnimation()
        }
    }

    private polling = async (execEnv: IExecutionEnvironment, onEnd: (execEnv: IExecutionEnvironment) => void, attempts?: number) => {
        if (!this.isPolling) return

        try {
            const response = await FlowApiClient.getFlowExecutionEnvironment(execEnv.uuid)
            this.sendFlowAnimation(response)

            if (response.status === 'failed') {
                this.isPolling = false
                onEnd(response)
            } else if (response.status === 'completed') {
                this.isPolling = false
                onEnd(response)
            } else {
                setTimeout(() => this.polling(response, onEnd), this.POLLING_INTERVAL)
            }
        } catch (e) {
            if (attempts <= this.MAX_ATTEMPTS) {
                setTimeout(() => this.polling(execEnv, onEnd), this.POLLING_INTERVAL, ((attempts ?? 0) + 1))
            } else {
                throw e
            }
        }
    }

    stopFlowExecution = async (flow: IFlow) => {
        this.isPolling = false

        FlowAnimationManager.forceStop()

        if (this.queue?.size() > 0) {
            console.error('There are still queued animations to run', this.queue)
        }

        const animGroup: IFlowAnimationGroup = { animations: [], depth: null }

        for (let i = 0; i < flow.nodes.length; i++) {
            const node = flow.nodes[i]
            animGroup.animations.push({ uuid: 'STOP-' + node.uuid, nodeId: node.uuid, nodeName: node.name, animation: 'waiting' })

            for (let k = 0; k < node.inputs.length; k++) {
                const input = node.inputs[k]
                const edge = FlowUIManager._encodeEdge(node, input)
                animGroup.animations.push({ uuid: 'STOP-' + node.uuid + edge, edgeId: edge, nodeId: node.uuid, nodeName: node.name, animation: 'waiting' })
            }
        }

        EventsEngine.publish('flow-animation', animGroup)
    }

    startFlowExecution = async (onEndPolling: (execEnv: IExecutionEnvironment) => void, onEndAnimation: () => void, flowRunConfig: IFlowRunConfig) => {
        try {
            const response = await FlowApiClient.startFlow(flowRunConfig)

            // !! WARNING !!
            // TODO: At the moment the fire and forget of the backend gives an error, so start flow for now only creates ExecutionEnvironment, then it is the frontend that executes executeFlow without await
            FlowApiClient.executeFlow(response.uuid)

            FlowAnimationManager.start(response.uuid, onEndPolling, onEndAnimation)

            return response
        } catch (error) {
            console.error(error)
            snackbar.show({ text: 'Something went wrong while running the flow. Please try again.', severity: 'error' })
            this.isPolling = false
            return null
        }
    }

    startFlowDryRunExecution = async (onEndPolling: (execEnv: IExecutionEnvironment) => void, onEndAnimation: () => void, flowRunConfig: IFlowRunConfig) => {
        try {
            const response = await FlowApiClient.startDryRun(flowRunConfig)

            // !! WARNING !!
            // TODO: At the moment the fire and forget of the backend gives an error, so start flow for now only creates ExecutionEnvironment, then it is the frontend that executes executeFlow without await
            FlowApiClient.executeFlow(response.uuid)

            FlowAnimationManager.start(response.uuid, onEndPolling, onEndAnimation)

            return response
        } catch (error) {
            console.error(error)
            snackbar.show({ text: 'Something went wrong while running the flow. Please try again.', severity: 'error' })
            this.isPolling = false
            return null
        }
    }
}

const FlowExecutionManager = new FlowExecutionManagerClass()
export default FlowExecutionManager
